import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { getLabAdminLevel } from '@/config/lab'
import { BmPage } from './BmPage'
import { buildEventCreatePayload } from './eventCreatePayload'
import './create-forms.css'

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

export function CreateEventPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [competitions, setCompetitions] = useState<Record<string, unknown>[]>([])
  const [name, setName] = useState('')
  const [competition, setCompetition] = useState('')
  const [openDate, setOpenDate] = useState('')
  const [entity, setEntity] = useState<Record<string, unknown>>({})

  useEffect(() => {
    void (async () => {
      try {
        const admin = getLabAdminLevel()
        const data =
          admin === 'Horse Racing'
            ? await bookmakerApi.competitionHorsePagination({})
            : await bookmakerApi.competitionAll({})
        setCompetitions(unwrapBmList(data))
      } catch (e) {
        setError(bmErrorMessage(e))
      }
    })()
  }, [])

  useEffect(() => {
    if (!id) return
    void (async () => {
      setBusy(true)
      try {
        const row = asRecord(await bookmakerApi.eventGetOne({ id }))
        setEntity(row)
        setName(String(row.name ?? ''))
        setCompetition(String(row.competition ?? row.competitionId ?? ''))
        setOpenDate(String(row.openDate ?? row.startTime ?? '').slice(0, 10))
      } catch (e) {
        setError(bmErrorMessage(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [id])

  async function onSubmit() {
    if (!name.trim()) {
      setError('Insert Valid Name')
      return
    }
    if (!competition) {
      setError('Select Competition')
      return
    }
    if (!openDate) {
      setError('Insert Valid Open Date')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (isEdit) {
        await bookmakerApi.eventEdit({
          ...entity,
          _id: id,
          id: entity.id ?? id,
          name: name.trim(),
          competition,
          openDate,
        })
      } else {
        const comp = competitions.find(
          (c) =>
            String(c.id) === competition ||
            String(c._id) === competition ||
            String(c.betfairId) === competition,
        )
        if (!comp) {
          setError('Select Competition')
          setBusy(false)
          return
        }
        await bookmakerApi.eventCreate(buildEventCreatePayload(comp, name, openDate))
      }
      navigate('/eventlist')
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title={isEdit ? 'Edit Event' : 'Create Event'}
      description="Lab create event — API assigns a new 9-digit custom event id"
      error={error}
    >
      <Card>
        <div className="bm-create">
          <div className="bm-create-row">
            <label htmlFor="bm-evt-name">Event Name</label>
            <input
              id="bm-evt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Insert Event Name"
            />
          </div>
          <div className="bm-create-row">
            <label htmlFor="bm-evt-comp">Competition Name</label>
            <select
              id="bm-evt-comp"
              value={competition}
              disabled={isEdit}
              onChange={(e) => setCompetition(e.target.value)}
            >
              <option value="">Select…</option>
              {competitions.map((c) => (
                <option
                  key={String(c._id ?? c.id)}
                  value={String(c.id ?? c.betfairId ?? c.competitionId)}
                >
                  {String(c.name ?? '')}
                </option>
              ))}
            </select>
          </div>
          <div className="bm-create-row">
            <label htmlFor="bm-evt-open">Open Date</label>
            <input
              id="bm-evt-open"
              type="date"
              value={openDate}
              onChange={(e) => setOpenDate(e.target.value)}
            />
          </div>
          <div className="bm-create-actions">
            <Button type="button" disabled={busy} onClick={() => void onSubmit()}>
              {busy ? 'Working…' : 'Submit'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/eventlist')}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </BmPage>
  )
}
